"use client";

import { useRef } from "react";
import { Knopf } from "../knopf";
import { Feld, type FeldEigenschaften } from "./feld";

/**
 * Dateiauswahl (M-002, Design-Guide `formularfelder.md`): eine Datei wählen, mit deutschem Text statt der Browser-Texte
 * („Choose File / No file chosen“). Sekundärer Knopf (`knopfText`, z. B. „Bild auswählen“) und daneben der Dateiname
 * bzw. `leerText` („Keine Datei ausgewählt“). Das echte Feld ist versteckt (`sr-only`, aus der Tab-Reihenfolge und für
 * Hilfstechnik ausgeblendet) und wird vom Knopf geöffnet; der Knopf ist Tastatur- und Screenreader-bedienbar. Der
 * gewählte Name kommt vom Aufrufer (`dateiname`), damit er ein Zurücksetzen mitmachen kann.
 *
 * Der Baustein prüft weder Typ noch Größe (wie `Dateifeld`): `accept` und `hilfe` sagen es dem Nutzer, die verbindliche
 * Prüfung liegt in der Action des Aufrufers (SG-1, SG-6).
 */
type Rest = Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "id" | "className" | "style" | "type" | "size" | "children" | "value" | "defaultValue" | "ref"
>;

export function Dateiauswahl({
  id,
  label,
  labelVersteckt,
  optional,
  hilfe,
  fehler,
  hinweissatz,
  feldhilfe,
  layout,
  groesse = "standard",
  knopfText = "Datei auswählen",
  leerText = "Keine Datei ausgewählt",
  dateiname,
  disabled,
  ...rest
}: FeldEigenschaften &
  Rest & {
    /** Beschriftung des Knopfs. */
    knopfText?: string;
    /** Text, solange keine Datei gewählt ist. */
    leerText?: string;
    /** Name der gewählten Datei; `null`/fehlend = keine Auswahl. */
    dateiname?: string | null;
  }) {
  const eingabe = useRef<HTMLInputElement | null>(null);
  return (
    <Feld id={id} label={label} labelVersteckt={labelVersteckt} optional={optional} hilfe={hilfe} fehler={fehler} hinweissatz={hinweissatz} feldhilfe={feldhilfe} layout={layout} groesse={groesse}>
      {(a) => (
        <>
          {/* Preline: component/file-input/file-input-basic-usage-example (Dateifeld versteckt, Knopf als Auslöser) */}
          <input
            {...rest}
            ref={eingabe}
            id={a.id}
            type="file"
            className="sr-only"
            tabIndex={-1}
            aria-hidden="true"
            disabled={disabled}
          />
          <div className="flex flex-wrap items-center gap-3">
            <Knopf groesse={groesse === "kompakt" ? "sm" : "md"} disabled={disabled} ariaDescribedby={a["aria-describedby"]} onClick={() => eingabe.current?.click()}>
              {knopfText}
            </Knopf>
            <span className="min-w-0 truncate text-sm text-muted-foreground-1" aria-live="polite">
              {dateiname ? dateiname : leerText}
            </span>
          </div>
        </>
      )}
    </Feld>
  );
}
